import React, { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, User, Clock, HelpCircle } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import api from "../lib/api";
import { usePageSeo } from "../lib/seo";
import BlogValuationBanner from "../components/valuation/BlogValuationBanner";

const SITE = "https://equitashomes.com";

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(undefined); // undefined = loading, null = not found
  const bodyRef = useRef(null);

  useEffect(() => {
    let active = true;
    setPost(undefined);
    api.get(`/blog/${slug}`).then(({ data }) => { if (active) setPost(data); }).catch(() => { if (active) setPost(null); });
    return () => { active = false; };
  }, [slug]);

  // Tag the FAQ heading in the rendered body so the "Jump to FAQ" chip can scroll to it.
  useEffect(() => {
    if (!post || !bodyRef.current) return;
    const h = [...bodyRef.current.querySelectorAll("h2,h3")].find(
      (el) => /frequently asked questions|^faqs?$/i.test(el.textContent.trim()));
    if (h) h.id = "post-faq";
  }, [post]);

  const bodyHtml = post?.body_html || "";
  const hasFaq = /frequently asked questions/i.test(bodyHtml) || (post?.faq?.length > 0);
  const readMins = post?.reading_time
    || `${Math.max(1, Math.round((bodyHtml.replace(/<[^>]+>/g, " ").trim().split(/\s+/).filter(Boolean).length) / 200))} min read`;
  const jumpToFaq = () => {
    document.getElementById("post-faq")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const canonical = `${SITE}/blog/${slug}`;
  const blogPosting = post ? {
    "@type": "BlogPosting", "@id": canonical,
    mainEntityOfPage: { "@type": "WebPage", "@id": canonical },
    headline: post.title,
    description: post.meta_description || post.excerpt || "",
    author: { "@type": "Person", name: "Louis Fong", jobTitle: "Broker", identifier: "DRE 01151598" },
    publisher: { "@type": "RealEstateAgent", "@id": `${SITE}/#brokerage`, name: "Equitas Homes", telephone: "+1-408-561-1321", url: SITE },
    datePublished: post.published_date || post.created_at || undefined,
    dateModified: post.modified_date || post.published_date || post.created_at || undefined,
    url: canonical,
  } : null;
  const brokerageSchema = {
    "@type": "RealEstateAgent", "@id": `${SITE}/#brokerage`, name: "Equitas Homes",
    telephone: "+1-408-561-1321", url: SITE, priceRange: "1% Listing Fee",
    address: { "@type": "PostalAddress", addressLocality: "San Jose", addressRegion: "CA", postalCode: "95125", addressCountry: "US" },
    areaServed: { "@type": "AdministrativeArea", name: "Santa Clara County" },
    founder: { "@type": "Person", "@id": `${SITE}/#agent`, name: "Louis Fong", jobTitle: "Broker", identifier: "DRE 01151598" },
  };
  const crumbs = post ? [
    { name: "Home", path: "/" },
    { name: "Blog", path: "/blog" },
    ...(post.city && post.landing_url ? [{ name: `Sell in ${post.city}`, path: post.landing_url }] : []),
    { name: post.title, path: `/blog/${slug}`, current: true },
  ] : [];
  const breadcrumbSchema = post ? {
    "@type": "BreadcrumbList", "@id": `${canonical}#breadcrumb`,
    itemListElement: crumbs.map((c, i) => ({
      "@type": "ListItem", position: i + 1, name: c.name,
      item: c.current ? canonical : `${SITE}${c.path}`,
    })),
  } : null;
  const faqSchema = post?.faq?.length ? {
    "@type": "FAQPage",
    mainEntity: post.faq.map((f) => ({
      "@type": "Question", name: f.question,
      acceptedAnswer: { "@type": "Answer", text: f.answer },
    })),
  } : null;
  usePageSeo(post ? {
    title: `${post.title} | Equitas Homes`,
    description: post.meta_description || post.excerpt || "",
    canonical,
    jsonLd: { "@context": "https://schema.org", "@graph": [blogPosting, brokerageSchema, breadcrumbSchema, ...(faqSchema ? [faqSchema] : [])] },
  } : { title: "Article | Equitas Homes", canonical });

  return (
    <div className="min-h-screen bg-brand-cream paper-grain flex flex-col">
      <style>{`
        .blog-prose { color: #3f4a44; line-height: 1.75; }
        .blog-prose h1,.blog-prose h2,.blog-prose h3,.blog-prose h4 { font-family: 'Cormorant Garamond', Georgia, serif; color: #14210f; font-weight: 600; line-height: 1.2; margin: 1.8rem 0 0.8rem; }
        .blog-prose h1 { font-size: 2rem; } .blog-prose h2 { font-size: 1.6rem; } .blog-prose h3 { font-size: 1.3rem; } .blog-prose h4 { font-size: 1.1rem; }
        .blog-prose p { margin: 0 0 1.1rem; }
        .blog-prose a { color: #1b3b2b; text-decoration: underline; }
        .blog-prose ul,.blog-prose ol { margin: 0 0 1.1rem 1.4rem; } .blog-prose ul { list-style: disc; } .blog-prose ol { list-style: decimal; }
        .blog-prose li { margin: 0.35rem 0; }
        .blog-prose strong { color: #14210f; }
        .blog-prose img { border-radius: 0.75rem; margin: 1.2rem 0; max-width: 100%; height: auto; }
        .blog-prose table { width: 100%; border-collapse: collapse; margin: 1.2rem 0; font-size: 0.9rem; }
        .blog-prose th,.blog-prose td { border: 1px solid #e4e0d6; padding: 0.6rem 0.8rem; text-align: left; }
        .blog-prose th { background: #f3f1ea; font-weight: 600; color: #14210f; }
        .blog-prose blockquote { border-left: 3px solid #b8892b; padding-left: 1rem; color: #6b7268; font-style: italic; margin: 1.2rem 0; }
      `}</style>
      <Header />
      <BlogValuationBanner compact />
      <main className="flex-1">
        <article className="mx-auto max-w-3xl px-5 sm:px-8 py-12 lg:py-16">
          <Link to="/blog" className="inline-flex items-center gap-1.5 text-sm font-medium text-brand-emerald hover:underline" data-testid="blog-back-link">
            <ArrowLeft className="h-3.5 w-3.5" /> All articles
          </Link>

          {post === undefined && <p className="mt-8 text-brand-stone" data-testid="blogpost-loading">Loading…</p>}

          {post === null && (
            <div className="mt-10" data-testid="blogpost-notfound">
              <h1 className="font-serif text-3xl text-brand-ink">Article not found</h1>
              <p className="mt-3 text-brand-stone">This post may have moved. Browse all articles instead.</p>
              <Link to="/blog" className="mt-5 inline-flex items-center gap-2 rounded-xl bg-brand-emerald px-5 py-3 text-sm font-semibold text-brand-cream">Back to the blog</Link>
            </div>
          )}

          {post && (
            <>
              <nav aria-label="Breadcrumb" className="mt-6" data-testid="blogpost-breadcrumb">
                <ol className="flex flex-wrap items-center gap-1.5 text-xs text-brand-stone/80">
                  {crumbs.map((c, i) => (
                    <li key={i} className="flex items-center gap-1.5">
                      {c.current ? (
                        <span className="text-brand-stone line-clamp-1 max-w-[12rem]" aria-current="page">{c.name}</span>
                      ) : (
                        <Link to={c.path} className="hover:text-brand-emerald hover:underline">{c.name}</Link>
                      )}
                      {i < crumbs.length - 1 && <span className="text-brand-stone/40">›</span>}
                    </li>
                  ))}
                </ol>
              </nav>
              {post.category && <span className="mt-6 block text-xs uppercase tracking-wider text-brand-gold font-semibold" data-testid="blogpost-category">{post.category}</span>}
              <h1 className="mt-3 font-serif font-light leading-[1.1] text-3xl sm:text-4xl lg:text-5xl text-brand-ink" data-testid="blogpost-title">{post.title}</h1>
              <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-brand-stone/80 border-b border-brand-hairline pb-6">
                <span className="inline-flex items-center gap-1.5"><User className="h-4 w-4" /> {post.author || "Louis Fong"}</span>
                <span className="inline-flex items-center gap-1.5" data-testid="blogpost-readtime"><Clock className="h-4 w-4" /> {readMins}</span>
                {(post.modified_date || post.published_date) && (
                  <span className="inline-flex items-center gap-1.5" data-testid="blogpost-updated">
                    Updated {new Date(post.modified_date || post.published_date).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
                  </span>
                )}
                {hasFaq && (
                  <button type="button" onClick={jumpToFaq} data-testid="blogpost-faq-jump"
                    className="inline-flex items-center gap-1.5 font-medium text-brand-emerald hover:underline">
                    <HelpCircle className="h-4 w-4" /> Jump to FAQ
                  </button>
                )}
              </div>
              <div ref={bodyRef} className="blog-prose mt-8" data-testid="blogpost-body" dangerouslySetInnerHTML={{ __html: bodyHtml || `<p>${post.excerpt || ""}</p>` }} />

              {post.city && post.landing_url && (
                <Link to={post.landing_url} data-testid="blogpost-city-guide-link"
                  className="mt-10 group flex items-center justify-between gap-4 rounded-2xl border border-brand-hairline bg-white p-5 hover:border-brand-emerald transition-colors">
                  <span className="flex items-start gap-3">
                    <ArrowRight className="h-5 w-5 text-brand-gold shrink-0 mt-0.5 rotate-[-45deg]" />
                    <span>
                      <span className="block text-xs uppercase tracking-wider text-brand-gold font-semibold">Local seller guide</span>
                      <span className="mt-1 block font-serif text-lg leading-snug text-brand-ink group-hover:text-brand-emerald transition-colors">Selling in {post.city}? See the full {post.city} home-value &amp; 1% listing guide</span>
                    </span>
                  </span>
                  <ArrowRight className="h-4 w-4 text-brand-emerald shrink-0" />
                </Link>
              )}

              <div className="mt-12 rounded-2xl bg-brand-emerald text-brand-cream p-7" data-testid="blogpost-cta">
                <p className="font-serif text-2xl">Curious what your home is worth?</p>
                <p className="mt-2 text-sm text-brand-cream/80">Get a free Multi-AI &amp; 3-Portal valuation with your real net equity at a 1% listing fee.</p>
                <Link to="/" className="mt-5 inline-flex items-center gap-2 rounded-xl bg-brand-cream px-5 py-3 text-sm font-semibold text-brand-emerald hover:bg-white transition-colors">Get my home value</Link>
                <p className="mt-4 text-xs text-brand-cream/70">
                  Curious how we reach the number? <Link to="/how-it-works" className="underline hover:text-brand-gold" data-testid="blogpost-how-link">See how the 3-Portal &amp; Multi-AI valuation works</Link>.
                </p>
              </div>

              {post.related?.length > 0 && (
                <div className="mt-14" data-testid="blogpost-related">
                  <h2 className="font-serif text-2xl text-brand-ink">Keep reading</h2>
                  <div className="mt-6 grid gap-5 sm:grid-cols-3">
                    {post.related.map((r) => (
                      <Link key={r.slug} to={`/blog/${r.slug}`} data-testid="related-card"
                        className="group flex flex-col rounded-2xl bg-white border border-brand-hairline p-5 hover:border-brand-emerald transition-colors">
                        {r.category && <span className="text-[0.62rem] uppercase tracking-wider text-brand-gold font-semibold">{r.category}</span>}
                        <h3 className="mt-1.5 font-serif text-lg leading-snug text-brand-ink group-hover:text-brand-emerald transition-colors line-clamp-3">{r.title}</h3>
                        <span className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-brand-emerald">Read <ArrowRight className="h-3.5 w-3.5" /></span>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </article>
      </main>
      <Footer />
    </div>
  );
}
